// The scroll-reveal engine: decides when, and how, content fades and rises in.
//
// React only renders a static `data-reveal` attribute (see useReveal). This
// module owns the one attribute that changes, `data-revealed`, and the CSS in
// Theme.js (MuiCssBaseline) does the animating with compositor-only keyframes.
// React never re-renders to reveal anything, and a parent re-render can never
// hide content again.
//
// It is built around the complaint that started it: "when I scroll fast,
// everything looks broken". A reveal that plays at reading pace while the
// visitor is flinging past makes content arrive late, which reads as broken.
// So every batch of newly visible elements gets a tier from the current scroll
// speed:
//
//   in       reading pace   full rise, staggered in reading order
//   quick    brisk scroll   a shorter, smaller rise, no stagger
//   instant  fling          no animation at all - the content is simply there
//
// and nothing is ever left hidden:
//
//   - anything already on screen, or above it, when it mounts is shown before
//     the first frame that could paint it (so above-the-fold content never
//     waits on JavaScript - good for LCP/SEO)
//   - anything skipped over during a fast scroll is shown when the scroll ends
//   - no IntersectionObserver, reduced motion, an automated browser or a
//     background tab: everything is shown at once
//   - each element reveals once and is then forgotten, so scrolling back up
//     never replays anything
//
// Geometry is only ever read inside requestAnimationFrame, never in the task
// that rendered the page. It used to be read in a microtask after every React
// commit, and each of those reads forced a full layout mid-render: on a phone
// the page's first render spent ~450ms there. A rAF callback runs before that
// frame's own style and layout, so the read costs nothing extra, several commits
// share one layout, and the attribute writes still land before anything paints.
//
// Tokens (durations, stagger, tiers) live in Theme.js `motion`.

import { tokens } from "../../../styles/Theme/Theme";

const { velocity: TIERS, staggerCap } = tokens.motion;
const ROOT_MARGIN = "0px 0px -8% 0px";
const SCROLL_END_FALLBACK = 150;

const pending = new Map(); // element -> options
let observer = null;
let queue = [];
let flushFrame = 0;
let sweepFrame = 0;
let settled = [];
let settleFrame = 0;
let speed = 0; // viewport heights per second, smoothed
let lastY = 0;
let lastT = 0;
let scrollEndTimer = 0;
let initialised = false;

const reducedMotionQuery =
  typeof window !== "undefined" && window.matchMedia
    ? window.matchMedia("(prefers-reduced-motion: reduce)")
    : null;

const motionAllowed = () =>
  typeof window !== "undefined" &&
  typeof IntersectionObserver !== "undefined" &&
  !reducedMotionQuery?.matches &&
  !navigator.webdriver &&
  document.visibilityState !== "hidden";

// Current scroll speed, decayed by the time since the last scroll event so a
// visitor who stopped a moment ago is not treated as still flinging.
const currentSpeed = () => {
  const idle = performance.now() - lastT;
  return speed * Math.exp(-idle / 120);
};

export const tierForSpeed = (vhPerSecond) =>
  vhPerSecond < TIERS.quick ? "in" : vhPerSecond < TIERS.instant ? "quick" : "instant";

// Reading order for a batch: top to bottom, then left to right within a row
// (rows are bucketed so a few px of misalignment does not reorder a grid).
export const readingOrder = (a, b) => {
  const rowA = Math.round(a.top / 24);
  const rowB = Math.round(b.top / 24);
  return rowA - rowB || a.left - b.left;
};

const reveal = (el, tier, index = 0) => {
  pending.delete(el);
  observer?.unobserve(el);
  if (tier === "in" && index > 0) {
    el.style.setProperty("--reveal-i", String(Math.min(index, staggerCap)));
  }
  el.setAttribute("data-revealed", tier);
};

const onIntersect = (entries) => {
  const tier = tierForSpeed(currentSpeed());
  entries
    .filter((entry) => entry.isIntersecting && pending.has(entry.target))
    .map((entry) => ({
      el: entry.target,
      top: entry.boundingClientRect?.top ?? 0,
      left: entry.boundingClientRect?.left ?? 0,
    }))
    .sort(readingOrder)
    .forEach(({ el }, i) => reveal(el, tier, tier === "in" ? i : 0));
};

// After a scroll settles: show whatever was flown past, and at the very bottom
// of the page show anything still waiting in the band the root margin trims.
const sweep = () => {
  sweepFrame = 0;
  if (!pending.size) return;
  const atBottom =
    window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
  const entries = [...pending.keys()].map((el) => [el, el.getBoundingClientRect()]);
  entries.forEach(([el, rect]) => {
    if (rect.bottom < 0) reveal(el, "instant");
    else if (atBottom && rect.top < window.innerHeight) reveal(el, "in");
  });
};

const scheduleSweep = () => {
  if (!sweepFrame && pending.size) sweepFrame = requestAnimationFrame(sweep);
};

const onScroll = () => {
  const now = performance.now();
  const y = window.scrollY;
  const dt = now - lastT;
  if (dt > 0 && dt < 500) {
    const instant = (Math.abs(y - lastY) / window.innerHeight) * (1000 / dt);
    speed = currentSpeed() * 0.5 + instant * 0.5;
  } else {
    speed = 0;
  }
  lastY = y;
  lastT = now;

  if (!("onscrollend" in window)) {
    clearTimeout(scrollEndTimer);
    scrollEndTimer = setTimeout(scheduleSweep, SCROLL_END_FALLBACK);
  }
};

// Once an entrance has played, drop it to a settled state: if the element is
// later hidden and shown again (a tab switch, a breakpoint) it must not replay.
// A staggered batch ends together, so the writes are batched into one frame.
const settle = () => {
  settleFrame = 0;
  settled.forEach((el) => {
    el.setAttribute("data-revealed", "done");
    if (el.style.getPropertyValue("--reveal-i")) el.style.removeProperty("--reveal-i");
  });
  settled = [];
};

const init = () => {
  if (initialised || typeof window === "undefined") return;
  initialised = true;
  lastY = window.scrollY;
  lastT = performance.now();
  if (typeof IntersectionObserver !== "undefined") {
    observer = new IntersectionObserver(onIntersect, { rootMargin: ROOT_MARGIN });
  }
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("scrollend", scheduleSweep, { passive: true });
  document.addEventListener("animationend", (e) => {
    const el = e.target;
    if (
      e.animationName.startsWith("reveal-") &&
      el instanceof Element &&
      el.hasAttribute("data-revealed") &&
      el.getAttribute("data-revealed") !== "done"
    ) {
      settled.push(el);
      if (!settleFrame) settleFrame = requestAnimationFrame(settle);
    }
  });
};

// Everything registered since the last frame, handled together at the start of
// the next one: every layout read first, then every attribute write.
const flush = () => {
  flushFrame = 0;
  const batch = queue.filter((el) => el.isConnected && pending.has(el));
  queue = [];
  if (!batch.length) return;

  if (!motionAllowed()) {
    batch.forEach((el) => reveal(el, "instant"));
    return;
  }

  const viewport = window.innerHeight;
  const rects = batch.map((el) => el.getBoundingClientRect());
  batch.forEach((el, i) => {
    if (rects[i].top < viewport) reveal(el, "instant");
    else observer.observe(el);
  });
};

export const register = (el, options = {}) => {
  init();
  pending.set(el, options);
  // No geometry needed for these two, so they are decided in the commit itself.
  if (!motionAllowed()) {
    reveal(el, "instant");
    return;
  }
  if (options.trigger === "mount") {
    reveal(el, "in", options.index || 0);
    return;
  }
  queue.push(el);
  if (!flushFrame) flushFrame = requestAnimationFrame(flush);
};

export const unregister = (el) => {
  pending.delete(el);
  observer?.unobserve(el);
};
